import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { EpisodeList } from '~/components/landing/EpisodeList';
import { SeasonSelector } from '~/components/landing/SeasonSelector';
import { useAnalytics } from '~/hooks/useAnalytics';
import type { PublicEpisodeInfo, PublicLandingPage } from '~/types';

export const Route = createFileRoute('/$slug/episodes')({
  component: EpisodesPage,
});

function EpisodesPage() {
  const router = useRouter();
  const { slug, landingPage } = Route.useRouteContext() as {
    slug: string;
    landingPage: PublicLandingPage;
  };
  const { trackEvent } = useAnalytics({ slug });

  // Track page view
  useEffect(() => {
    trackEvent('view');
  }, [trackEvent]);

  // Group episodes by season
  const seasonMap = useMemo(() => {
    if (!landingPage.episodes) return new Map<string, PublicEpisodeInfo[]>();

    const map = new Map<string, PublicEpisodeInfo[]>();
    for (const ep of landingPage.episodes) {
      const season = ep.seasonName ?? 'Season 1';
      const list = map.get(season) ?? [];
      list.push(ep);
      map.set(season, list);
    }
    return map;
  }, [landingPage.episodes]);

  const seasonNames = useMemo(() => Array.from(seasonMap.keys()), [seasonMap]);
  const [activeSeason, setActiveSeason] = useState<string>('');

  useEffect(() => {
    if (seasonNames.length > 0 && !activeSeason) {
      setActiveSeason(seasonNames[0]);
    }
  }, [seasonNames, activeSeason]);

  const activeEpisodes = useMemo(
    () => seasonMap.get(activeSeason) ?? [],
    [seasonMap, activeSeason],
  );

  const handleSelectEpisode = useCallback(
    (episodeId: string) => {
      router.navigate({
        to: '/$slug/game/$episodeId',
        params: { slug, episodeId },
      });
    },
    [router, slug],
  );

  const { branding, show, content } = landingPage;

  const brandingStyles: React.CSSProperties = {
    '--brand-primary': branding.primaryColor,
    '--brand-secondary': branding.secondaryColor,
    '--brand-bg': branding.backgroundColor,
  } as React.CSSProperties;

  return (
    <div
      className="h-[100dvh] w-screen flex items-center justify-center p-0 lg:p-8 xl:p-16"
      style={{
        backgroundImage: 'url(https://s3.amazonaws.com/yak.assets/52xP82c.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        ...brandingStyles,
      }}
    >
      <div className="w-full h-full lg:h-auto lg:max-w-7xl lg:max-h-[90vh] lg:rounded-2xl overflow-hidden bg-white/95 lg:shadow-2xl flex flex-col">
        {/* Header area - logo, title, description */}
        <div className="flex flex-col items-center text-center px-4 pt-6 pb-4 md:px-8 md:pt-8 flex-shrink-0">
          {branding.logoUrl && (
            <img
              src={branding.logoUrl}
              alt={show?.title ?? 'Logo'}
              className="h-12 md:h-16 w-auto object-contain mb-3"
            />
          )}
          {show?.title && (
            <h1
              className="text-xl md:text-2xl lg:text-3xl font-bold"
              style={{ color: branding.primaryColor }}
            >
              {show.title}
            </h1>
          )}
          {show?.description && (
            <p
              className="text-sm md:text-base max-w-2xl mt-2 opacity-80"
              style={{ color: branding.secondaryColor }}
            >
              {show.description}
            </p>
          )}
          <p className="text-sm md:text-base text-gray-500 mb-4 text-center mt-3">
            Select an Episode to Play!
          </p>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto px-4 pb-6 md:px-8 md:pb-8">
          {/* Season Selector */}
          <div className="mb-4">
            <SeasonSelector
              seasons={seasonNames}
              activeSeason={activeSeason}
              onSelectSeason={setActiveSeason}
              branding={branding}
            />
          </div>
          {/* Episode List */}
          <EpisodeList
            episodes={activeEpisodes}
            onSelectEpisode={handleSelectEpisode}
            branding={branding}
          />
        </div>
      </div>
    </div>
  );
}
